import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from './useAuth'

export function useProfileCompletion() {
  const { user } = useAuth()
  const [isProfileComplete, setIsProfileComplete] = useState<boolean | null>(null)
  const [loading, setLoading] = useState(true)

  const checkProfileCompletion = useCallback(async () => {
    if (!user?.id) {
      setIsProfileComplete(null)
      setLoading(false)
      return
    }

    try {
      setLoading(true)

      const { data: profile, error } = await supabase
        .from('profiles')
        .select('name, phone, zip_code, address_number')
        .eq('id', user.id)
        .maybeSingle()

      if (error) {
        console.error('[useProfileCompletion] Erro ao verificar perfil:', error)
        setIsProfileComplete(false)
        setLoading(false)
        return
      }

      // Se não existe perfil, considerar incompleto
      if (!profile) {
        setIsProfileComplete(false)
        setLoading(false)
        return
      }

      // Perfil completo se tem nome e telefone preenchidos
      const hasName = Boolean(profile?.name && profile.name.trim().length > 0)
      const hasPhone = Boolean((profile as any)?.phone && (profile as any).phone.trim().length > 0)
      setIsProfileComplete(hasName && hasPhone)
    } catch (error) {
      console.error('[useProfileCompletion] Erro ao verificar completude do perfil:', error)
      setIsProfileComplete(false)
    } finally {
      setLoading(false)
    }
  }, [user?.id])

  const createIncompleteProfileNotification = useCallback(async () => {
    if (!user?.id) return false

    try {
      
      // Verificar se já existe uma notificação não lida de cadastro incompleto
      const { data: existingNotification, error: checkError } = await supabase
        .from('notifications')
        .select('id, is_read')
        .eq('user_id', user.id)
        .eq('type', 'system')
        .eq('is_read', false)
        .ilike('title', '%cadastro%')
        .maybeSingle()

      if (checkError) {
        console.error('[useProfileCompletion] Erro ao verificar notificação existente:', checkError)
      }

      if (existingNotification && !existingNotification.is_read) {
        return true
      }
      
      // Criar nova notificação (sem marcar as antigas como lidas - contador acumulativo)
      const { data, error } = await supabase
        .from('notifications')
        .insert({
          user_id: user.id,
          type: 'system',
          title: 'Finalize seu cadastro',
          message: 'Você ainda não finalizou seu cadastro. Clique aqui para completar suas informações.',
          data: {
            action: 'complete_profile',
            redirect_to: '/profile'
          },
          is_read: false
        })
        .select()

      if (error) {
        console.error('[useProfileCompletion] Erro ao criar notificação de cadastro incompleto:', error)
        return false
      }

      // Aguardar um pouco para garantir que o realtime subscription capture
      // E disparar um evento customizado para forçar atualização do contador
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('notification-created'))
      }, 500)
      
      return true
    } catch (error) {
      console.error('[useProfileCompletion] Erro ao criar notificação:', error)
      return false
    }
  }, [user?.id])

  const removeIncompleteProfileNotification = useCallback(async () => {
    if (!user?.id) return

    try {
      await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('user_id', user.id)
        .eq('type', 'system')
        .ilike('title', '%cadastro%')
    } catch (error) {
      console.error('Erro ao remover notificação de cadastro incompleto:', error)
    }
  }, [user?.id])

  useEffect(() => {
    checkProfileCompletion()
  }, [checkProfileCompletion])

  return {
    isProfileComplete,
    loading,
    checkProfileCompletion,
    createIncompleteProfileNotification,
    removeIncompleteProfileNotification
  }
}
